import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import React, { useEffect, useReducer, useRef } from 'react';
import type { ReactNode } from 'react';

import { DIALOG_TYPE as DIALOG_TYPE_CONFIRMATION } from 'components/Dialogs/ConfirmationDialog';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import type { StandardDrawerVariant } from 'components/Drawers/standardDrawerContract';
import { useAppSelector } from 'hooks/redux';

import './modalRegistrations';
import { getRootModalDrawerPresentationOptions, getRootModalPresentation } from './drawerPresentation';
import ModalRenderer from './ModalRenderer';
import {
  getLoadedModalComponent,
  isModalComponentPending,
  isModalComponentPreloadable,
  preloadModalComponent,
} from './modalRegistry';
import RootModalDrawerRenderer from './RootModalDrawerRenderer';
import { useRootModalUrlDialogs } from './useRootModalUrlDialogs';
import { useShowDialogQueryParam } from './useShowDialogQueryParam';

function RootModal(): ReactNode {
  useShowDialogQueryParam();

  const webDrawerInput = useAppSelector((state) =>
    Boolean(featureFlagsSelectors.getFeatureFlag(state, 'webDrawerInput')),
  );
  const modals = useRootModalUrlDialogs();
  // A lazily registered modal renders nothing until its code loads, and MUI gives focus to the modal that mounts
  // last. Dialogs that aren't rendered yet wait behind a modal that is still loading, until its load succeeds or
  // fails, so they mount above it as they would if it were eager. Dialogs already rendered stay put.
  const [, noteModalLoadSettled] = useReducer((count: number) => count + 1, 0);
  const renderedKeysRef = useRef<ReadonlySet<string>>(new Set());
  const renderedKeys = new Set<string>();
  let isWaitingForLoad = false;

  const modalArray = modals.toArray();
  const drawerEntries: RootModalDrawerOptions[] = [];
  // A drawer dispatched while a true modal is already rendered below it has to
  // join the modal plane. MUI ModalManager already treats the later-mounted
  // drawer as the top layer for focus and aria-hidden, so paint order has to
  // agree or the drawer ends up under the dialog's backdrop. This remains true
  // for every later record in the ordered traversal.
  let hasUnderlyingModal = false;

  const renderedModals = modalArray.map((modal, index) => {
    const key = `${modal.type} ${modal.id}`;
    const isNew = !renderedKeysRef.current.has(key);
    if (isNew && isModalComponentPreloadable(modal.type) && !getLoadedModalComponent(modal.type)) {
      // Start a new dialog's load here, so one that waits loads alongside the modal ahead of it, and a retry after a
      // failed load counts as loading. ModalRenderer reuses the same load.
      preloadModalComponent(modal.type)?.catch(() => undefined);
    }
    if (isWaitingForLoad && isNew) {
      return null;
    }
    renderedKeys.add(key);

    const presentation = getRootModalPresentation(modal);
    // Overlay drawers use the nearest underlying drawer options so child modals
    // can overlay it without each dispatcher opting in.
    const { drawerOptions, plane, underlyingDrawerOptions } = getRootModalDrawerPresentationOptions({
      drawerEntries,
      hasUnderlyingModal,
      index,
      modalArray,
      webDrawerInput,
    });
    const canRenderAsOverlayDrawer = modal.type !== DIALOG_TYPE_CONFIRMATION && presentation !== 'modal';
    const shouldRenderAsDrawer =
      presentation !== 'modal' && (drawerOptions || (underlyingDrawerOptions && canRenderAsOverlayDrawer));

    if (!shouldRenderAsDrawer) {
      // Confirmations are excluded so they stay authoritative above whatever
      // opened them instead of pushing later drawers onto the modal plane.
      if (modal.type !== DIALOG_TYPE_CONFIRMATION) {
        hasUnderlyingModal = true;
      }
      if (isModalComponentPending(modal.type)) {
        isWaitingForLoad = true;
      }
      return <ModalRenderer key={key} modal={modal} dialogType={modal.type} onLoadSettled={noteModalLoadSettled} />;
    }

    const drawerVariant: StandardDrawerVariant = underlyingDrawerOptions ? 'overlay' : 'temporary';
    // Overlay drawers should visually belong to the base drawer: no extra
    // scrim, and inherit the underlying width unless the launched drawer
    // requested its own width.
    const resolvedDrawerOptions: RootModalDrawerOptions = {
      ...(drawerOptions ?? {}),
      showScrim: drawerVariant === 'temporary' ? (drawerOptions?.showScrim ?? true) : false,
      width:
        drawerVariant === 'overlay'
          ? (drawerOptions?.width ?? underlyingDrawerOptions?.width ?? 'Medium')
          : (drawerOptions?.width ?? 'Medium'),
    };
    drawerEntries.push(resolvedDrawerOptions);

    return (
      <RootModalDrawerRenderer
        key={key}
        drawerOptions={resolvedDrawerOptions}
        modal={modal}
        plane={plane}
        variant={drawerVariant}
      />
    );
  });

  useEffect(() => {
    renderedKeysRef.current = renderedKeys;
  });

  return renderedModals;
}

export default React.memo(RootModal);
